import { createLogic } from 'redux-logic';
import { AxiosResponse, Method } from 'axios';

import { appActions } from '..';
import {
  getErrorMessage,
  dispatchActionForAppAndUser,
  trackModularReduxRegistrationError
} from '../../utils';

import accessLogic from './access';
import registrationLogic from './registration';
import fundingLogic from './funding';
import customAsyncLogic from './custom';

export const HOST_URL = publicRuntimeConfig =>
  `${publicRuntimeConfig.webregServiceURL}/api/v1`;
export const TEST_URL = publicRuntimeConfig =>
  `${publicRuntimeConfig.webregServiceURL}`;
export const REGISTRATION_URL = publicRuntimeConfig =>
  `${publicRuntimeConfig.registrationServiceURL}/api/v1/registration`;

type AxiosConfig = {
  method: Method;
  url: string;
  data?: Record<string, any>;
  headers?: Record<string, any>;
};

type AxiosConfigBuilder = (
  payload: any,
  publicRuntimeConfig: any,
  getState: any
) => AxiosConfig;

export type AsyncLogicParams = {
  name: string;
  axiosConfig?: AxiosConfig | AxiosConfigBuilder | null;
  promise?: Promise<any> | ((...args: any) => Promise<any>);
  registrationStep?: string;
  onSuccess?: (data: Response) => void;
  shouldSetGlobalAsyncProperties?: boolean;
};

export const parseAxiosConfig = (
  axiosConfig,
  payload,
  publicRuntimeConfig,
  getState
) => {
  let config = axiosConfig;
  if (typeof axiosConfig === 'function') {
    config = axiosConfig(payload, publicRuntimeConfig, getState);
  }
  if (config.method === 'get') {
    return config;
  }
  const data = config.data || payload;
  return { ...config, data };
};

export const processAsyncRequest = (
  promise,
  httpClient,
  axiosConfig,
  payload
) => {
  if (!promise && !axiosConfig) {
    throw new Error(
      'Must include an axios config or a promise for async calls.'
    );
  } else if (promise) {
    return promise(payload);
  }
  return httpClient(axiosConfig);
};

const webregAsyncLogic = ({
  name,
  axiosConfig,
  promise,
  registrationStep,
  shouldSetGlobalAsyncProperties = true
}: AsyncLogicParams) =>
  createLogic({
    name,
    type: name,
    process: (
      { httpClient, publicRuntimeConfig, getState, action }: any,
      dispatch,
      done
    ) => {
      if (shouldSetGlobalAsyncProperties) dispatch(appActions.asyncLoading());
      dispatchActionForAppAndUser(`${action.type}Loading`, dispatch);

      const config = axiosConfig
        ? parseAxiosConfig(
            axiosConfig,
            action.payload,
            publicRuntimeConfig,
            getState
          )
        : null;

      processAsyncRequest(promise, httpClient, config, action.payload)
        .then((response: AxiosResponse) => {
          dispatchActionForAppAndUser(
            `${action.type}Success`,
            dispatch,
            response.data
          );
          if (shouldSetGlobalAsyncProperties)
            dispatch(appActions.asyncSuccess(response.data));
          done();
        })
        .catch((error: Error) => {
          const message = getErrorMessage(error);
          if (registrationStep) {
            trackModularReduxRegistrationError({
              eventProps: {
                registration_step: registrationStep,
                error_message: message
              }
            });
          }

          dispatchActionForAppAndUser(`${action.type}Failure`, dispatch, error);
          if (shouldSetGlobalAsyncProperties)
            dispatch(appActions.asyncFailure(message));

          done();
        });
    }
  });

const arrLogic: Array<AsyncLogicParams> = [
  ...accessLogic,
  ...registrationLogic,
  ...fundingLogic
];

const appLogic = arrLogic
  .reduce((prev: any[], curr: any) => prev.concat(webregAsyncLogic(curr)), [])
  .concat(customAsyncLogic);

export default appLogic;
